/**
 * How far back a stats surface reads. Every market card — the phone floor,
 * the TV board, the admin movers — quotes the same windows, so "tonight"
 * means the same night everywhere. Pure: the server turns the bound into a
 * ledger `at` filter and the clients into a query param.
 */

/** The windows a stats surface can be cut by, in display order. */
export type StatsWindow = 'tonight' | '24h' | '7d' | 'all';

export const STATS_WINDOWS: readonly StatsWindow[] = ['tonight', '24h', '7d', 'all'];

/** The default read: a casino night starts today, so the cards open on it. */
export const DEFAULT_STATS_WINDOW: StatsWindow = 'tonight';

/** True when `raw` names a window the server and the surfaces both know. */
export function isStatsWindow(raw: unknown): raw is StatsWindow {
  return typeof raw === 'string' && (STATS_WINDOWS as readonly string[]).includes(raw);
}

/** The chip the picker prints. */
export function windowLabel(window: StatsWindow): string {
  switch (window) {
    case 'tonight':
      return 'Tonight';
    case '24h':
      return '24 hours';
    case '7d':
      return '7 days';
    case 'all':
      return 'All time';
  }
}

/**
 * The window's lower bound in epoch milliseconds, or null when the window is
 * unbounded. "Tonight" runs from local midnight — a night at the tables is
 * a local fact, not a UTC one.
 */
export function windowSince(window: StatsWindow, now: number): number | null {
  switch (window) {
    case 'tonight': {
      const d = new Date(now);
      d.setHours(0, 0, 0, 0);
      return d.getTime();
    }
    case '24h':
      return now - 24 * 60 * 60_000;
    case '7d':
      return now - 7 * 24 * 60 * 60_000;
    case 'all':
      return null;
  }
}
